import type { FileReviewDocumentKind, FileReviewPanelPlacement } from './types';

const OPEN_KEY = 'jarvis.fileReview.panelOpen';
const PLACEMENT_KEY = 'jarvis.fileReview.panelPlacement';
const SIDE_KEY = 'jarvis.fileReview.panelSide';
const WIDTH_KEY = 'jarvis.fileReview.panelWidth';
const AUTO_HIDE_KEY = 'jarvis.fileReview.autoHideDiscussionPanel';

const LEGACY_PDF_COMMENTS_OPEN_KEY = 'jarvis.pdfViewer.commentsOpen';
const LEGACY_SESSION_COMMENTS_KEY = 'jarvis.pdfCommentsPanelOpen';

export const PLACEMENT_CHANGED_EVENT = 'jarvis-file-review-placement-changed';
export const AUTO_HIDE_CHANGED_EVENT = 'jarvis-file-review-auto-hide-changed';

export type FileReviewDiscussionSide = 'left' | 'right';

function readBoolPref(storageKey: string, legacyKeys: string[] = [], defaultValue = false): boolean {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored === '0') return false;
    if (stored === '1') return true;

    for (const legacyKey of legacyKeys) {
      const legacy = localStorage.getItem(legacyKey) ?? sessionStorage.getItem(legacyKey);
      if (legacy === '0' || legacy === '1') {
        localStorage.setItem(storageKey, legacy);
        sessionStorage.removeItem(legacyKey);
        return legacy === '1';
      }
    }
  } catch {
    /* storage unavailable */
  }
  return defaultValue;
}

function writeBoolPref(storageKey: string, value: boolean): void {
  try {
    localStorage.setItem(storageKey, value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export function readFileReviewPanelOpen(documentType?: FileReviewDocumentKind): boolean {
  const key = documentType ? `${OPEN_KEY}.${documentType}` : OPEN_KEY;
  const legacy =
    documentType === 'pdf' ? [LEGACY_PDF_COMMENTS_OPEN_KEY, LEGACY_SESSION_COMMENTS_KEY] : [];
  return readBoolPref(key, legacy, false);
}

export function writeFileReviewPanelOpen(open: boolean, documentType?: FileReviewDocumentKind): void {
  writeBoolPref(documentType ? `${OPEN_KEY}.${documentType}` : OPEN_KEY, open);
  if (documentType === 'pdf') {
    writeBoolPref(LEGACY_PDF_COMMENTS_OPEN_KEY, open);
  }
}

export function readFileReviewDiscussionSide(): FileReviewDiscussionSide {
  try {
    const stored = localStorage.getItem(SIDE_KEY);
    if (stored === 'left' || stored === 'right') return stored;
    const placement = localStorage.getItem(PLACEMENT_KEY);
    if (placement === 'left' || placement === 'right') return placement;
  } catch {
    /* ignore */
  }
  return 'right';
}

function rememberDiscussionSide(placement: FileReviewPanelPlacement): void {
  if (placement !== 'left' && placement !== 'right') return;
  try {
    localStorage.setItem(SIDE_KEY, placement);
  } catch {
    /* ignore */
  }
}

export function readFileReviewPanelPlacement(documentType?: FileReviewDocumentKind): FileReviewPanelPlacement {
  try {
    if (documentType) {
      const specific = localStorage.getItem(`${PLACEMENT_KEY}.${documentType}`);
      if (specific === 'left' || specific === 'right' || specific === 'off') return specific;
    }
    const stored = localStorage.getItem(PLACEMENT_KEY);
    if (stored === 'left' || stored === 'right' || stored === 'off') return stored;
  } catch {
    /* ignore */
  }
  return 'off';
}

export function writeFileReviewPanelPlacement(
  placement: FileReviewPanelPlacement,
  documentType?: FileReviewDocumentKind
): void {
  try {
    rememberDiscussionSide(placement);
    const key = documentType ? `${PLACEMENT_KEY}.${documentType}` : PLACEMENT_KEY;
    localStorage.setItem(key, placement);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(PLACEMENT_CHANGED_EVENT));
    }
  } catch {
    /* ignore */
  }
}

/** Enable discussion on the saved side when placement is currently off. */
export function enableFileReviewDiscussionPlacement(
  documentType?: FileReviewDocumentKind
): FileReviewDiscussionSide {
  const side = readFileReviewDiscussionSide();
  writeFileReviewPanelPlacement(side, documentType);
  return side;
}

export function readAutoHideDiscussionPanel(): boolean {
  return readBoolPref(AUTO_HIDE_KEY, [], false);
}

export function writeAutoHideDiscussionPanel(autoHide: boolean): void {
  writeBoolPref(AUTO_HIDE_KEY, autoHide);
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(AUTO_HIDE_CHANGED_EVENT));
    }
  } catch {
    /* ignore */
  }
}

export function readFileReviewPanelWidth(documentType?: FileReviewDocumentKind): number | null {
  try {
    const key = documentType ? `${WIDTH_KEY}.${documentType}` : WIDTH_KEY;
    const raw = sessionStorage.getItem(key) ?? localStorage.getItem(key);
    if (!raw) return null;
    const num = Number.parseInt(raw, 10);
    return Number.isFinite(num) ? num : null;
  } catch {
    return null;
  }
}

export function writeFileReviewPanelWidth(width: number, documentType?: FileReviewDocumentKind): void {
  try {
    const key = documentType ? `${WIDTH_KEY}.${documentType}` : WIDTH_KEY;
    sessionStorage.setItem(key, String(width));
  } catch {
    /* ignore */
  }
}
